import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";
import { useBackToClose } from "../../utils/backToClose";

const METRIC_FIELDS = [
  { key: "connectionsSent", label: "Connections Sent" },
  { key: "inMailsSent", label: "InMails Sent" },
  { key: "emailsExtracted", label: "Emails Extracted" },
  { key: "directCalls", label: "Direct Calls" },
  { key: "dataEmailsExtracted", label: "Data Emails Extracted" },
  { key: "screenerResponsesReceived", label: "Screener Responses Received" },
];

const todayISO = () => new Date().toISOString().split("T")[0];

const mapLogToForm = (log) => ({
  connectionsSent: log?.connectionsSent ?? 0,
  inMailsSent: log?.inMailsSent ?? 0,
  emailsExtracted: log?.emailsExtracted ?? 0,
  directCalls: log?.directCalls ?? 0,
  dataEmailsExtracted: log?.dataEmailsExtracted ?? 0,
  screenerResponsesReceived: log?.screenerResponsesReceived ?? 0,
  date: log?.date || todayISO(),
});

function ActivityLogModal({
  show,
  onClose,
  onSubmit,
  initialLog = null,
  isSubmitting = false,
  errorMessage = "",
}) {
  useBackToClose(show, onClose);
  const [form, setForm] = useState(() => mapLogToForm(initialLog));
  const [dateError, setDateError] = useState("");

  if (!show) return null;

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "date") setDateError("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!form.date) {
      setDateError("Date is required.");
      return;
    }

    onSubmit({
      ...METRIC_FIELDS.reduce(
        (acc, field) => ({ ...acc, [field.key]: Number(form[field.key]) || 0 }),
        {}
      ),
      date: form.date,
    });
  };

  const isEditMode = Boolean(initialLog);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 dark:bg-black/60 p-3 sm:p-6">
      <button
        type="button"
        aria-label="Close activity log modal"
        onClick={onClose}
        className="absolute inset-0"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="activity-log-modal-title"
        className="relative w-full max-w-2xl rounded-2xl border border-app-primary bg-app-card shadow-2xl dark:shadow-black/50"
      >
        <div className="flex items-start justify-between border-b border-app-primary px-5 py-4 sm:px-6">
          <div>
            <h3 id="activity-log-modal-title" className="text-xl font-semibold text-app-heading">
              {isEditMode ? "Edit Activity Log" : "Log Activity"}
            </h3>
            <p className="text-sm text-app-muted">
              Record daily outreach activity for this project.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-app-muted transition-colors hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-5 sm:px-6">
          {errorMessage && (
            <div className="mb-4 rounded-lg border border-app-error bg-app-error px-3 py-2 text-sm text-app-error">
              {errorMessage}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {METRIC_FIELDS.map((field) => (
              <div key={field.key}>
                <label htmlFor={field.key} className="mb-1 block text-sm font-medium text-app-secondary">
                  {field.label}
                </label>
                <input
                  id={field.key}
                  type="number"
                  min="0"
                  step="1"
                  value={form[field.key]}
                  onChange={(event) => updateField(field.key, event.target.value)}
                  className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                />
              </div>
            ))}

            <div>
              <label htmlFor="activity-date" className="mb-1 block text-sm font-medium text-app-secondary">
                Date
              </label>
              <input
                id="activity-date"
                type="date"
                required
                value={form.date}
                onChange={(event) => updateField("date", event.target.value)}
                className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
              />
              {dateError && <p className="mt-1 text-xs text-app-error">{dateError}</p>}
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3 border-t border-app-primary pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-app-primary px-5 py-2 text-sm font-semibold text-app-secondary transition hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg border border-[#2470B7] bg-[#2470B7] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#1f5f9b] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500"
            >
              {isSubmitting ? "Saving..." : isEditMode ? "Save Changes" : "Save Activity"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ActivityLogModal;
